export type AudioLevelResult = {
	significantAudioRatio: number;
	peakRms: number;
	totalSamples: number;
};

// RMS threshold below which a sample is considered silence/white noise.
// Typical speech produces RMS values 0.02-0.3; background noise is 0.001-0.01.
const SILENCE_RMS_THRESHOLD = 0.015;

// Minimum ratio of samples that must exceed the threshold to be considered
// "has meaningful audio content."
export const MINIMUM_SIGNIFICANT_AUDIO_RATIO = 0.1;

const SAMPLE_INTERVAL_MS = 100;

export type AudioLevelMonitor = {
	start: () => void;
	stop: () => AudioLevelResult;
};

export function createAudioLevelMonitor(
	stream: MediaStream,
): AudioLevelMonitor | null {
	const AudioContextClass =
		window.AudioContext ?? (window as any).webkitAudioContext;
	if (!AudioContextClass) return null;

	let audioContext: AudioContext | null = null;
	let analyser: AnalyserNode | null = null;
	let source: MediaStreamAudioSourceNode | null = null;
	let intervalId: ReturnType<typeof setInterval> | null = null;
	let samples: number[] = [];

	function sampleRms(): number {
		if (!analyser) return 0;
		const dataArray = new Float32Array(analyser.fftSize);
		analyser.getFloatTimeDomainData(dataArray);
		let sumSquares = 0;
		for (let i = 0; i < dataArray.length; i++) {
			sumSquares += dataArray[i] * dataArray[i];
		}
		return Math.sqrt(sumSquares / dataArray.length);
	}

	return {
		start() {
			samples = [];
			try {
				audioContext = new AudioContextClass();
				analyser = audioContext.createAnalyser();
				analyser.fftSize = 2048;
				source = audioContext.createMediaStreamSource(stream);
				source.connect(analyser);
			} catch (e) {
				console.error("AudioLevelMonitor: failed to connect to stream", e);
				// If we can't connect, we'll return empty results (no silence rejection)
				return;
			}

			intervalId = setInterval(() => {
				samples.push(sampleRms());
			}, SAMPLE_INTERVAL_MS);
		},

		stop(): AudioLevelResult {
			if (intervalId) {
				clearInterval(intervalId);
				intervalId = null;
			}
			// Take one final sample only if analyser was connected
			if (analyser) {
				samples.push(sampleRms());
			}

			if (source) {
				source.disconnect();
				source = null;
			}
			if (audioContext) {
				audioContext.close().catch(() => {});
				audioContext = null;
			}
			analyser = null;

			const totalSamples = samples.length;
			if (totalSamples === 0) {
				return { significantAudioRatio: 0, peakRms: 0, totalSamples: 0 };
			}

			let significantCount = 0;
			let peakRms = 0;
			for (const rms of samples) {
				if (rms > peakRms) peakRms = rms;
				if (rms > SILENCE_RMS_THRESHOLD) significantCount++;
			}

			return {
				significantAudioRatio: significantCount / totalSamples,
				peakRms,
				totalSamples,
			};
		},
	};
}

export function isRecordingSilent(result: AudioLevelResult): boolean {
	// If no samples were collected (e.g., AudioContext failed to connect),
	// we cannot determine silence — assume the recording is valid.
	if (result.totalSamples === 0) return false;
	return result.significantAudioRatio < MINIMUM_SIGNIFICANT_AUDIO_RATIO;
}
